<template>
  <!-- Incoming Call -->
  <Teleport to="body">
    <Transition name="call-overlay">
      <div
        v-if="callStore.callStatus === 'ringing' && callStore.isIncomingCall"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div
          class="relative w-full max-w-md mx-4 bg-zinc-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          <div
            class="relative h-40 bg-gradient-to-b from-green-900/60 to-zinc-900 flex flex-col items-center justify-center gap-2">
            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span
                v-for="i in 3"
                :key="i"
                class="absolute rounded-full border border-green-400/30 animate-ping"
                :style="{
                  width: 60 + i * 30 + 'px',
                  height: 60 + i * 30 + 'px',
                  animationDelay: i * 0.3 + 's',
                  animationDuration: '1.5s',
                }" />
            </div>
            <div
              class="relative z-10 w-16 h-16 rounded-full bg-green-600 flex items-center justify-center shadow-lg shadow-green-600/40 overflow-hidden">
              <img v-if="avatarUrl" :src="avatarUrl" class="w-full h-full object-cover" />
              <span v-else class="text-2xl font-bold text-white">{{ initials }}</span>
            </div>
            <span class="relative z-10 text-xs text-green-300 font-medium tracking-widest uppercase">{{
              tt('Incoming Call', 'Dohodni klic')
            }}</span>
          </div>
          <div class="p-6">
            <h2 class="text-xl font-semibold text-white text-center mb-1">{{ displayName }}</h2>
            <p class="text-sm text-zinc-400 text-center mb-6">
              {{
                callStore.activeCall?.type === 'video' ? tt('Video call', 'Video klic') : tt('Audio call', 'Avdio klic')
              }}
            </p>
            <div class="flex gap-3">
              <button
                @click="callStore.rejectCall(callStore.activeCall!.id)"
                class="flex-1 flex items-center justify-center gap-2 py-3.5 bg-red-600 hover:bg-red-500 rounded-xl text-white font-medium transition-colors">
                <span class="material-symbols-outlined text-xl">call_end</span>{{ tt('Decline', 'Zavrni') }}
              </button>
              <button
                @click="callStore.answerCall(callStore.activeCall!.id)"
                class="flex-1 flex items-center justify-center gap-2 py-3.5 bg-green-600 hover:bg-green-500 rounded-xl text-white font-medium transition-colors">
                <span class="material-symbols-outlined text-xl">call</span>{{ tt('Answer', 'Sprejmi') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Outgoing Call (Calling) -->
  <Teleport to="body">
    <Transition name="call-overlay">
      <div
        v-if="callStore.callStatus === 'calling'"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <div
          class="relative w-full max-w-md mx-4 bg-zinc-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          <div
            class="relative h-40 bg-gradient-to-b from-blue-900/60 to-zinc-900 flex flex-col items-center justify-center gap-2">
            <div
              class="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/40 overflow-hidden">
              <img v-if="avatarUrl" :src="avatarUrl" class="w-full h-full object-cover" />
              <span v-else class="text-2xl font-bold text-white">{{ initials }}</span>
            </div>
            <span class="text-xs text-blue-300 font-medium tracking-widest uppercase">{{
              tt('Calling...', 'Klicanje...')
            }}</span>
          </div>
          <div class="p-6">
            <h2 class="text-xl font-semibold text-white text-center mb-1">{{ displayName }}</h2>
            <p class="text-sm text-zinc-400 text-center mb-6">{{ tt('Ringing...', 'Zvoni...') }}</p>
            <button
              @click="callStore.endCall()"
              class="w-full flex items-center justify-center gap-2 py-3.5 bg-red-600 hover:bg-red-500 rounded-xl text-white font-medium transition-colors">
              <span class="material-symbols-outlined text-xl">call_end</span>{{ tt('Cancel', 'Prekliči') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Active Call -->
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="callStore.callStatus === 'active'" class="fixed inset-0 z-40 flex flex-col bg-black">
        <div class="flex-1 flex items-center justify-center">
          <div class="text-center text-white">
            <div
              class="w-28 h-28 mx-auto mb-4 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden ring-4 ring-green-500/30">
              <img v-if="avatarUrl" :src="avatarUrl" class="w-full h-full object-cover" />
              <span v-else class="text-4xl font-bold">{{ initials }}</span>
            </div>
            <p class="text-xl font-semibold mb-1">{{ displayName }}</p>
            <p class="text-sm text-zinc-400 font-mono">{{ callDuration }}</p>
          </div>
        </div>

        <audio ref="remoteAudioRef" autoplay />

        <div
          class="flex items-center justify-center gap-4 px-6 py-5 bg-zinc-900/95 backdrop-blur-sm border-t border-white/5">
          <button
            @click="callStore.toggleMute()"
            class="flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-colors min-w-[72px]"
            :class="callStore.isMuted ? 'bg-red-600/30 text-red-400' : 'bg-zinc-800 hover:bg-zinc-700 text-white'">
            <span class="material-symbols-outlined text-2xl">{{ callStore.isMuted ? 'mic_off' : 'mic' }}</span>
            <span class="text-[10px]">{{
              callStore.isMuted ? tt('Unmute', 'Vklopi zvok') : tt('Mute', 'Izklopi zvok')
            }}</span>
          </button>
          <button
            @click="callStore.endCall()"
            class="flex flex-col items-center gap-1 px-6 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl transition-colors min-w-[72px]">
            <span class="material-symbols-outlined text-2xl">call_end</span>
            <span class="text-[10px]">{{ tt('End', 'Končaj') }}</span>
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useCallStore } from '../stores/call';
import { useSocketStore } from '../stores/socket';
import { useImageAssetsStore } from '../stores/imageAssets';
import { useAuthStore } from '../stores/auth';
import { getImageContentUrl, resolveAnnounceAudioUrl } from '../services/mediaLibraryUrls';
import { startLivePlayback, addPCMChunk, stopLivePlayback } from '../services/liveAudioPlayback';
import { tt } from '../utils/consoleText';
import { personName } from '../utils/personName';
import { localeTag } from '../utils/formatDate';

const callStore = useCallStore();
const socketStore = useSocketStore();
const imageAssetsStore = useImageAssetsStore();
const authStore = useAuthStore();
const remoteAudioRef = ref<HTMLAudioElement | null>(null);
const callDuration = ref('0:00');
let durationTimer: ReturnType<typeof setInterval> | null = null;
let callStartTime: number | null = null;

const otherParticipant = computed(() => callStore.otherParticipant);

const displayName = computed(() => {
  const p = otherParticipant.value;
  // A person is named by their name, else the address they are reached through
  // (`personName`) — never by the derived handle. The rest of the chain is
  // unchanged: the call target's own label, the callee id, then `Unknown`.
  if (p) return personName(p, callStore.callTargetName ?? tt('Unknown', 'Neznano'));
  if (callStore.callTargetName) return callStore.callTargetName;
  const call = callStore.activeCall;
  if (call?.calleeId) return call.calleeId;
  return tt('Unknown', 'Neznano');
});

const initials = computed(() => {
  const name = displayName.value;
  if (!name || name === tt('Unknown', 'Neznano')) return '?';
  const parts = name.split(' ');
  return parts.length > 1 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name[0].toUpperCase();
});

const avatarUrl = ref<string | null>(null);
/** Drops an out-of-order answer when the participant changes mid-load. */
let avatarLoadId = 0;

/**
 * The other participant's avatar, in the order this app can actually load it.
 *
 * `avatarImageId` is an `images` row (`entity_type=user`, `image_type=avatar`) —
 * NOT a file under `/uploads`: the hub has no `/api/uploads/…` route at all and
 * no file there is named by the id, so the hand-built
 * `config.apiUrl + '/uploads/' + id` was a 404 in every mode — and in cloud mode
 * it asked the CLOUD origin for a hub path (the class `resolveImageUrl` closed,
 * card t_1574d279).
 *
 * 1. `imageAssetsStore.fetchImageUrl` — the bytes over the API layer (Bearer over
 *    HTTP, or the WebRTC data channel in a cloud session), reusing whatever the
 *    users / people screens already prefetched.
 * 2. `getImageContentUrl` — the hub's own `/api/images/<id>/content?token=…`
 *    (`authenticateAny` accepts the query token; a raw `<img>` cannot send a
 *    Bearer header). That resolver yields a hub origin or nothing, so the worst
 *    case here is the initials fallback below — never the cloud origin.
 */
async function loadAvatar(imageId: string | null): Promise<void> {
  const run = ++avatarLoadId;
  if (!imageId) {
    avatarUrl.value = null;
    return;
  }
  const blobUrl = await imageAssetsStore.fetchImageUrl(imageId);
  if (run !== avatarLoadId) return;
  avatarUrl.value = blobUrl ?? getImageContentUrl(imageId, authStore.token);
}

watch(
  () => otherParticipant.value?.avatarImageId ?? null,
  (imageId) => void loadAvatar(imageId),
  { immediate: true },
);

// Pipe remote stream to audio element
watch(
  () => callStore.remoteStream,
  (stream) => {
    if (remoteAudioRef.value && stream) {
      remoteAudioRef.value.srcObject = stream;
    }
  },
  { immediate: true },
);

// Duration timer
watch(
  () => callStore.callStatus,
  (status) => {
    if (status === 'active') {
      callStartTime = Date.now();
      durationTimer = setInterval(() => {
        if (!callStartTime) return;
        const elapsed = Math.floor((Date.now() - callStartTime) / 1000);
        const mins = Math.floor(elapsed / 60);
        const secs = elapsed % 60;
        callDuration.value = mins + ':' + secs.toString().padStart(2, '0');
      }, 1000);
    } else {
      if (durationTimer) {
        clearInterval(durationTimer);
        durationTimer = null;
      }
      callStartTime = null;
      callDuration.value = '0:00';
    }
  },
);

// Initialize call store socket listeners (globally, once)
function onBroadcastStart() {
  startLivePlayback();
}
function onBroadcastChunk(d: { data: ArrayBuffer }) {
  addPCMChunk(d.data);
}
function onBroadcastStop() {
  stopLivePlayback();
}
function onAnnounce(payload: { text?: string; audioUrl?: string; priority?: string }) {
  if (payload.audioUrl) {
    // A hub path (`/uploads/announce/<file>`, `/uploads/tts/<file>`) may only be
    // fetched from a HUB origin — `resolveAnnounceAudioUrl` returns one or null,
    // never the cloud app, whose HTML 404 is what used to make this announcement
    // silent (card t_46b30a53, the avatar card's sibling). Null means the clip is
    // unreachable from here (a cloud session with no hub in reach): make no
    // request at all. The `else if` is deliberately NOT taken — a hub that sent
    // audio also sent `text`, and speaking it here would double the announcement
    // with `stores/userDevices.ts`, which already falls back to speech.
    const url = resolveAnnounceAudioUrl(payload.audioUrl);
    if (url) {
      fetch(url)
        .then((r) => r.arrayBuffer())
        .then((buf) => {
          const ctx = new AudioContext();
          return ctx.decodeAudioData(buf).then((decoded) => {
            const src = ctx.createBufferSource();
            src.buffer = decoded;
            src.connect(ctx.destination);
            src.start(0);
          });
        })
        .catch(() => undefined);
    }
  } else if (payload.text && 'speechSynthesis' in window) {
    const utt = new SpeechSynthesisUtterance(payload.text);
    // The voice follows the APP's language (`localeTag`, `src/utils/formatDate.ts`),
    // not the machine's: the hub's announce copy is written for this panel's
    // locale, and an utterance with no `lang` is voiced by whatever language the
    // runtime happens to be set to. The Web Speech API picks the closest installed
    // voice for the tag, so a device with no voice for it falls back rather than
    // going silent.
    utt.lang = localeTag();
    window.speechSynthesis.speak(utt);
  }
}

onMounted(() => {
  callStore.initialize();
  socketStore.on('userDevice:broadcast:start', onBroadcastStart);
  socketStore.on('userDevice:broadcast:chunk', onBroadcastChunk);
  socketStore.on('userDevice:broadcast:stop', onBroadcastStop);
  socketStore.on('userDevice:announce', onAnnounce);
});

onUnmounted(() => {
  if (durationTimer) {
    clearInterval(durationTimer);
  }
  socketStore.off('userDevice:broadcast:start', onBroadcastStart);
  socketStore.off('userDevice:broadcast:chunk', onBroadcastChunk);
  socketStore.off('userDevice:broadcast:stop', onBroadcastStop);
  socketStore.off('userDevice:announce', onAnnounce);
  stopLivePlayback();
});
</script>

<style scoped>
.call-overlay-enter-active,
.call-overlay-leave-active {
  transition: opacity 0.3s ease;
}
.call-overlay-enter-from,
.call-overlay-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
